import { zodResolver } from '@hookform/resolvers/zod';
import {
  API_KEY_ADMIN_SCOPE,
  type ApiKeyCreated,
  type CreateApiKey,
  type CreateApiKeyInput,
  CreateApiKeySchema,
  PERMISSION_KEYS,
} from '@servicetim/shared';
import { useCreateApiKey, useProjects } from '@servicetim/ui/api';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Switch,
} from '@servicetim/ui/components';
import { useId, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { CheckboxList, type CheckboxListGroup } from '../common/checkbox-list';
import { FormDialog } from '../common/form-dialog';
import { FormField, fieldA11y } from '../common/form-field';
import { fromLocalDateTimeInput, toLocalDateTimeInput } from '../common/format';
import { useFormSubmit } from '../common/use-form-submit';
import { SecretBox } from './secret-box';

/** Permission keys grouped by prefix, plus the `admin` pseudo-scope for configuration endpoints. */
export function scopeGroups(): CheckboxListGroup[] {
  const byPrefix = new Map<string, string[]>();
  for (const key of PERMISSION_KEYS) {
    const prefix = key.split('.')[0] ?? key;
    byPrefix.set(prefix, [...(byPrefix.get(prefix) ?? []), key]);
  }
  return [
    {
      title: 'Configuration',
      options: [
        {
          id: API_KEY_ADMIN_SCOPE,
          label: 'admin',
          description: 'Tenant configuration endpoints (/admin/v1)',
        },
      ],
    },
    ...[...byPrefix.entries()].map(([prefix, keys]) => ({
      title: prefix,
      options: keys.map((key) => ({ id: key, label: key })),
    })),
  ];
}

/**
 * Two phases: the form, then the created key with its secret shown exactly once. Closing the
 * dialog after creation is the only way out, so the admin acknowledges the secret.
 */
export function CreateApiKeyDialog({ onClose }: { onClose: () => void }) {
  const [created, setCreated] = useState<ApiKeyCreated | null>(null);
  if (created) {
    return (
      <Dialog open onOpenChange={(open) => (open ? undefined : onClose())}>
        <DialogContent hideClose>
          <DialogHeader>
            <DialogTitle>API key created</DialogTitle>
            <DialogDescription>
              Use it as <code className="font-mono">Authorization: Bearer stk_…</code>.
            </DialogDescription>
          </DialogHeader>
          <SecretBox created={created} />
          <DialogFooter>
            <Button onClick={onClose}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
  return <CreateApiKeyForm onCreated={setCreated} onClose={onClose} />;
}

function CreateApiKeyForm({
  onCreated,
  onClose,
}: {
  onCreated: (created: ApiKeyCreated) => void;
  onClose: () => void;
}) {
  const id = useId();
  const create = useCreateApiKey();
  const projects = useProjects({ pageSize: 200 });
  const [restrict, setRestrict] = useState(false);
  const groups = useMemo(scopeGroups, []);
  const form = useForm<CreateApiKeyInput, unknown, CreateApiKey>({
    resolver: zodResolver(CreateApiKeySchema),
    defaultValues: { name: '', scopes: [], projectIds: null, expiresAt: null },
  });
  const submit = useFormSubmit(form);
  const { errors } = form.formState;
  const scopesError = errors.scopes?.message ?? errors.scopes?.root?.message;
  const projectsError = errors.projectIds?.message ?? errors.projectIds?.root?.message;

  return (
    <FormDialog
      open
      onOpenChange={(open) => (open ? undefined : onClose())}
      title="New API key"
      description="A service user acts through the key with exactly these scopes. The secret is shown once after creation."
      formId={`${id}-form`}
      submitLabel="Create key"
      size="lg"
      busy={submit.busy}
      error={submit.error}
      omitPaths={submit.omitPaths}
      onSubmit={form.handleSubmit(async (values) => {
        const result = await submit.run(() => create.mutateAsync(values));
        if (result) onCreated(result);
      })}
    >
      <FormField label="Name" htmlFor={`${id}-name`} required error={errors.name?.message}>
        <Input
          {...form.register('name')}
          {...fieldA11y(`${id}-name`, { error: errors.name })}
          maxLength={80}
          placeholder="Zapier"
          autoComplete="off"
        />
      </FormField>

      <div className="space-y-1.5">
        <p id={`${id}-scopes-label`} className="font-medium text-fg text-sm leading-none">
          Scopes{' '}
          <span aria-hidden="true" className="text-red-600 dark:text-red-400">
            *
          </span>
        </p>
        <p id={`${id}-scopes-description`} className="text-fg-muted text-xs">
          Pick the permissions the integration needs; `admin` unlocks configuration endpoints.
        </p>
        <Controller
          control={form.control}
          name="scopes"
          render={({ field }) => (
            <CheckboxList
              id={`${id}-scopes`}
              aria-labelledby={`${id}-scopes-label`}
              aria-describedby={`${id}-scopes-description`}
              aria-invalid={Boolean(scopesError)}
              groups={groups}
              columns={3}
              value={field.value ?? []}
              onChange={(next) => field.onChange(next)}
              className="max-h-64 overflow-y-auto rounded-md border border-border p-3"
            />
          )}
        />
        {scopesError ? (
          <p role="alert" className="text-red-600 dark:text-red-400 text-xs">
            {scopesError}
          </p>
        ) : null}
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Switch
            id={`${id}-restrict`}
            checked={restrict}
            onCheckedChange={(checked) => {
              setRestrict(checked);
              form.setValue('projectIds', checked ? [] : null, { shouldDirty: true });
              form.clearErrors('projectIds');
            }}
          />
          <Label htmlFor={`${id}-restrict`}>Restrict to projects</Label>
        </div>
        {restrict ? (
          <Controller
            control={form.control}
            name="projectIds"
            render={({ field }) => (
              <CheckboxList
                id={`${id}-projects`}
                aria-label="Allowed projects"
                aria-invalid={Boolean(projectsError)}
                columns={2}
                options={projects.items.map((p) => ({
                  id: p.id,
                  label: p.name,
                  description: p.key,
                }))}
                value={field.value ?? []}
                onChange={(next) => field.onChange(next)}
                className="max-h-40 overflow-y-auto rounded-md border border-border p-3"
              />
            )}
          />
        ) : (
          <p className="text-fg-muted text-xs">Every project the service user can reach.</p>
        )}
        {projectsError ? (
          <p role="alert" className="text-red-600 dark:text-red-400 text-xs">
            {projectsError}
          </p>
        ) : null}
      </div>

      <FormField
        label="Expires"
        htmlFor={`${id}-expires`}
        description="Optional; the key stops working after this moment."
        error={errors.expiresAt?.message}
      >
        <Controller
          control={form.control}
          name="expiresAt"
          render={({ field }) => (
            <Input
              type="datetime-local"
              {...fieldA11y(`${id}-expires`, { description: true, error: errors.expiresAt })}
              value={toLocalDateTimeInput(field.value)}
              onChange={(event) => field.onChange(fromLocalDateTimeInput(event.target.value))}
              onBlur={field.onBlur}
              className="w-auto"
            />
          )}
        />
      </FormField>
    </FormDialog>
  );
}
